import { formatLiraValue, liraUnitSchema, validateLiraAnalytics, validateLiraVisualization, createLiraAnalytics, migrationCompositionDataset, type DashboardBarangayStatistics, type LiraDataset, type LiraAnalytics, type LiraReportTable, type LiraVisualization } from "@rbim/shared";
import { Download, FileSpreadsheet, Printer } from "lucide-react";
import { useRef } from "react";
import "./rbim-analytics.css";

export function RbimAnalyticsPanel({ analytics, compact = false, expectedBarangayId }: { analytics: LiraAnalytics; compact?: boolean; expectedBarangayId?: string | null }) {
  const printRef = useRef<HTMLElement>(null);
  if (expectedBarangayId !== undefined && analytics.scope?.barangayId !== expectedBarangayId) return <p role="status">The analytics scope does not match the current Barangay context. Request fresh analytics.</p>;
  const reportProblem = analytics.datasets ? validateLiraAnalytics(analytics, expectedBarangayId) : null;
  if (reportProblem) return <p className="rbim-chart-warning" role="status">{reportProblem}</p>;
  return (
    <section ref={printRef} className={compact ? "rbim-analytics rbim-analytics-compact" : "rbim-analytics"} aria-label="RBIM visual analytics">
      <header className="rbim-analytics-header">
        <div>
          <strong>Visual analytics</strong>
          <small>{analytics.sourceLabel}</small>
          <small>Generated {formatDateTime(analytics.generatedAt)}</small>
        </div>
        <div className="rbim-analytics-actions">
          {analytics.table ? (
            <button type="button" onClick={() => downloadExcelCompatible(analytics.table!, analytics)}>
              <FileSpreadsheet aria-hidden="true" /> Excel
            </button>
          ) : null}
          <button type="button" onClick={() => printAnalytics(printRef.current)}>
            <Printer aria-hidden="true" /> PDF / Print
          </button>
        </div>
      </header>

      {analytics.warnings?.map((warning) => <p key={warning} className="rbim-chart-warning" role="status">{warning}</p>)}
      <div className="rbim-analytics-charts">
        {analytics.visualizations.map((visualization) => (
          <VisualizationCard key={visualization.id} analytics={analytics} visualization={visualization} compact={compact} {...(expectedBarangayId !== undefined ? { expectedBarangayId } : {})} />
        ))}
      </div>

      {analytics.table ? <AnalyticsTable table={analytics.table} /> : null}
    </section>
  );
}

function VisualizationCard({ analytics, visualization, compact, expectedBarangayId }: { analytics: LiraAnalytics; visualization: LiraVisualization; compact: boolean; expectedBarangayId?: string | null }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const problem = validateLiraVisualization(analytics, visualization, expectedBarangayId);
  if (problem) return <p className="rbim-chart-warning" role="status">{problem}</p>;
  const dataset = analytics.datasets!.find((d) => d.id === visualization.datasetId)!;
  const shown: LiraVisualization = { ...visualization, title: tidyTitle(visualization.title) };
  return (
    <article className="rbim-chart-card">
      <header>
        <div><strong>{shown.title}</strong><small>{visualization.seriesLabel}</small></div>
        <button
          type="button"
          className="rbim-chart-download"
          onClick={() => void (svgRef.current ? downloadSvgAsPng(svgRef.current, shown.title) : undefined)}
          aria-label={`Download ${shown.title} as PNG`}
        >
          <Download aria-hidden="true" /> PNG
        </button>
      </header>
      {visualization.type === "DONUT"
        ? <DonutChart chartRef={svgRef} visualization={shown} dataset={dataset} />
        : visualization.type === "LINE"
          ? <LineChart chartRef={svgRef} visualization={shown} />
          : <BarChart chartRef={svgRef} visualization={shown} compact={compact} />}
    </article>
  );
}

function wrapLabel(value: string, maximum: number) {
  const lines: string[] = [];
  let remaining = value;
  while (remaining.length > maximum) {
    const space = remaining.lastIndexOf(" ", maximum);
    const cut = space > maximum / 2 ? space : maximum;
    lines.push(remaining.slice(0, cut)); remaining = remaining.slice(cut).trimStart();
  }
  lines.push(remaining); return lines;
}
function headerHeight(title: string) { return 38 + wrapLabel(title, 110).length * 18; }
function ChartHeading({ visualization }: { visualization: LiraVisualization }) {
  return <g>{wrapLabel(visualization.title, 110).map((line, i) => <text key={i} x="18" y={22 + i * 18} className="rbim-chart-title">{line}</text>)}<text x="18" y={headerHeight(visualization.title) - 10} className="rbim-chart-label">{visualization.seriesLabel}</text></g>;
}
function BarChart({ chartRef, visualization }: { chartRef: React.RefObject<SVGSVGElement | null>; visualization: LiraVisualization; compact: boolean }) {
  const values = visualization.data.flatMap((p) => p.value === null ? [] : [p.value]);
  const minimum = Math.min(0, ...values), maximum = Math.max(0, ...values);
  const span = maximum - minimum || 1;
  const plotLeft = 330, plotWidth = 430;
  const x = (value: number) => plotLeft + (value - minimum) / span * plotWidth;
  const zero = x(0);
  let rowY = headerHeight(visualization.title) + 18;
  const rows = visualization.data.map((item) => {
    const lines = wrapLabel(item.label, 38);
    const y = rowY; rowY += Math.max(40, lines.length * 18 + 12);
    return { ...item, y, lines };
  });
  return <svg ref={chartRef} viewBox={"0 0 1120 " + (rowY + 28)} className="rbim-chart-svg" role="img" aria-label={visualization.title}>
    <title>{visualization.title}</title><ChartHeading visualization={visualization} />
    <line x1={zero} x2={zero} y1={headerHeight(visualization.title)} y2={rowY} className="rbim-chart-zero" />
    {rows.map((item, i) => <g key={i}><title>{item.label + ": " + formatLiraValue(item.value, visualization.unit)}</title>
      {item.lines.map((line, n) => <text key={n} x={plotLeft - 12} y={item.y + 15 + n * 18} textAnchor="end" className="rbim-chart-label">{line}</text>)}
      {item.value !== null ? <rect x={Math.min(x(item.value), zero)} y={item.y} width={Math.abs(x(item.value) - zero)} height="21" rx="3" className="rbim-chart-bar" /> : null}
      <text x="780" y={item.y + 15} className="rbim-chart-value">{formatLiraValue(item.value, visualization.unit)}</text>
    </g>)}
    <text x={plotLeft} y={rowY + 18} className="rbim-chart-label">{formatLiraValue(minimum, visualization.unit)}</text>
    <text x={plotLeft + plotWidth} y={rowY + 18} textAnchor="end" className="rbim-chart-label">{formatLiraValue(maximum, visualization.unit)}</text>
  </svg>;
}
function DonutChart({ chartRef, visualization, dataset }: { chartRef: React.RefObject<SVGSVGElement | null>; visualization: LiraVisualization; dataset: LiraDataset }) {
  const total = dataset.composition!.total;
  const radius = 72, circumference = 2 * Math.PI * radius;
  const top = headerHeight(visualization.title), cy = top + 105;
  const palette = ["rbim-donut-a", "rbim-donut-b", "rbim-donut-c", "rbim-donut-d"];
  let offset = 0, y = top + 25;
  const rows = dataset.points.map((p) => { const lines = wrapLabel(p.label, 45); const row = { p, lines, y }; y += Math.max(45, lines.length * 18 + 25); return row; });
  return <svg ref={chartRef} viewBox={"0 0 900 " + Math.max(cy + 105, y + 20)} className="rbim-chart-svg rbim-donut-svg" role="img" aria-label={visualization.title}>
    <title>{visualization.title}</title><ChartHeading visualization={visualization} />
    <circle cx="135" cy={cy} r={radius} className="rbim-donut-track" />
    {dataset.points.map((p, i) => { const raw = p.rawCount ?? p.value!; const dash = raw / total * circumference; const position = offset; offset += dash;
      return <circle key={i} cx="135" cy={cy} r={radius} className={palette[i % palette.length]} style={{ transform: "rotate(-90deg)", transformOrigin: "135px " + cy + "px" }} strokeDasharray={dash + " " + (circumference - dash)} strokeDashoffset={-position}><title>{p.label + ": " + formatLiraValue(p.value, dataset.unit)}</title></circle>; })}
    <text x="135" y={cy - 6} textAnchor="middle" className="rbim-donut-total-label">Composition total</text>
    <text x="135" y={cy + 20} textAnchor="middle" className="rbim-donut-total">{formatLiraValue(total)}</text>
    {rows.map(({ p, lines, y: rowY }, i) => <g key={i}>
      <rect x="280" y={rowY - 12} width="14" height="14" rx="3" className={palette[i % palette.length]} />
      {lines.map((line, n) => <text key={n} x="305" y={rowY + n * 18} className="rbim-donut-legend-label">{line}</text>)}
      <text x="305" y={rowY + lines.length * 18} className="rbim-donut-legend-value">{formatLiraValue(p.value, dataset.unit)}{dataset.unit === "COUNT" ? " (" + formatLiraValue((p.rawCount ?? p.value!) / total * 100, "PERCENTAGE") + " of composition total)" : ""}</text>
    </g>)}
  </svg>;
}
function LineChart({ chartRef, visualization }: { chartRef: React.RefObject<SVGSVGElement | null>; visualization: LiraVisualization }) {
  const values = visualization.data.flatMap((p) => p.value === null ? [] : [p.value]);
  const min = Math.min(0, ...values), max = Math.max(0, ...values), span = max - min || 1;
  const times = visualization.data.map((p) => p.time!);
  const start = Math.min(...times), end = Math.max(...times);
  const top = headerHeight(visualization.title) + 25, height = 170;
  const points = visualization.data.map((p) => ({ ...p, x: 110 + (p.time! - start) / (end - start) * 650, y: p.value === null ? null : top + height - (p.value - min) / span * height }));
  let path = "", previous = false;
  for (const p of points) { if (p.y === null) { previous = false; continue; } path += (previous ? " L " : " M ") + p.x + " " + p.y; previous = true; }
  return <svg ref={chartRef} viewBox={"0 0 900 " + (top + height + 65)} className="rbim-chart-svg" role="img" aria-label={visualization.title}>
    <title>{visualization.title}</title><ChartHeading visualization={visualization} />
    <line x1="110" x2="760" y1={top + height} y2={top + height} className="rbim-chart-zero" />
    <path d={path} className="rbim-line-path" />
    {points.map((p, i) => <g key={i}><title>{p.label + ": " + formatLiraValue(p.value, visualization.unit)}</title>
      {p.y !== null ? <circle cx={p.x} cy={p.y} r="5" className="rbim-line-point" /> : null}
      <text x={p.x} y={top + height + 25} textAnchor="middle" className="rbim-chart-label">{p.label}</text>
      <text x={p.x} y={p.y === null ? top + height / 2 : p.y - 12} textAnchor="middle" className="rbim-chart-value">{formatLiraValue(p.value, visualization.unit)}</text>
    </g>)}
    <text x="100" y={top + 8} textAnchor="end" className="rbim-chart-label">{formatLiraValue(max, visualization.unit)}</text>
    <text x="100" y={top + height} textAnchor="end" className="rbim-chart-label">{formatLiraValue(min, visualization.unit)}</text>
  </svg>;
}

function AnalyticsTable({ table }: { table: LiraReportTable }) {
  return (
    <section className="rbim-analytics-table-wrap">
      <h3>{tidyTitle(table.title)}</h3>
      <div className="responsive-table">
        <table className="rbim-analytics-table">
          <thead><tr>{table.columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
          <tbody>
            {table.rows.length ? table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>{table.columns.map((column) => <td key={column.key}>{formatCell(row[column.key], column.format, column.unit ?? (column.unitKey ? liraUnitSchema.safeParse(row[column.unitKey]).data : undefined))}</td>)}</tr>
            )) : <tr><td colSpan={table.columns.length}>No authorized RBIM data is available for this report.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function analyticsFromBarangays(input: {
  scopeLabel: string;
  generatedAt?: string;
  rows: Array<{ barangayName: string; population: number; households: number; families: number; nonMigrants: number; migrants: number; transient: number }>;
}): LiraAnalytics {
  const scope = { label: input.scopeLabel, barangayId: null };
  const population: LiraDataset = { id: "report-population-by-barangay", metric: "POPULATION", title: "Population by Barangay", scope, unit: "COUNT", calculation: "COUNT", dimension: "BARANGAY", order: "SOURCE", points: input.rows.map((row, i) => ({ key: String(i), label: row.barangayName, value: row.population })) };
  const rows: DashboardBarangayStatistics[] = input.rows.map((r, i) => ({ ...r, barangayId: String(i), psgcCode: "" }));
  return createLiraAnalytics(scope, [population, migrationCompositionDataset(scope, rows)], "BAR", input.generatedAt).analytics;
}

function downloadExcelCompatible(table: LiraReportTable, analytics: LiraAnalytics) {
  const lines = [
    ["RBIM Report", table.title],
    ["Scope", analytics.sourceLabel],
    ["Generated", formatDateTime(analytics.generatedAt)],
    ["Source", "Authorized submitted RBIM records"],
    [],
    table.columns.map((column) => column.label),
    ...table.rows.map((row) => table.columns.map((column) => String(row[column.key] ?? ""))),
  ];
  const content = lines.map((line) => line.map(tsvCell).join("\t")).join("\r\n");
  downloadBlob(new Blob(["\ufeff", content], { type: "application/vnd.ms-excel;charset=utf-8" }), `${safeFileName(table.title)}.xls`);
}

function tidyTitle(title: string): string {
  const parts = title.split(/\s+—\s+/u);
  while (parts.length > 2 && parts[parts.length - 1]!.trim().toLowerCase() === parts[parts.length - 2]!.trim().toLowerCase()) parts.pop();
  return parts.join(" — ");
}

// Print only the clicked analytics panel in a separate document. Portal print CSS
// hides .lira-widget and would otherwise print the Dashboard behind it.
function printAnalytics(panel: HTMLElement | null) {
  if (!panel) return;
  const popup = window.open("", "_blank", "width=1100,height=850");
  if (!popup) {
    window.alert("LIRA print window blocked. Allow pop-ups for this portal, then try again.");
    return;
  }
  const report = popup.document;
  report.title = "RBIM Visual Analytics Report";
  const style = report.createElement("style");
  style.textContent = "@page { size: A4 portrait; margin: 12mm; }\nhtml, body { margin:0; padding:0; color:#173968; background:white; font-family:Arial,Helvetica,sans-serif; }\nh1 { font-size:18pt; margin:0 0 14px; }\n.rbim-analytics { display:grid; gap:14px; color:#263c55; }\n.rbim-analytics-header { display:flex; justify-content:space-between; margin-bottom:6px; }\n.rbim-analytics-header strong { display:block; font-size:13pt; }\n.rbim-analytics-header small { display:block; margin-top:4px; font-size:9pt; color:#53677c; }\n.rbim-analytics-actions,.rbim-chart-download,button { display:none!important; }\n.rbim-analytics-charts { display:block; }\n.rbim-chart-card { page-break-inside:avoid; break-inside:avoid; margin-bottom:12px; border:1px solid #dce5ec; }\n.rbim-chart-card>header { padding:8px; border-bottom:1px solid #dce5ec; }\n.rbim-chart-card>header strong { font-size:10pt; }\n.rbim-chart-card>header small { display:block; font-size:9pt; }\n.rbim-chart-svg { box-sizing:border-box; width:100%!important; min-width:0!important; height:auto; display:block; background:white; }\n.rbim-chart-title { fill:#173968; font-size:14px; font-weight:700; }\n.rbim-chart-label,.rbim-donut-legend-label { fill:#53677c; font-size:11px; }\n.rbim-chart-value,.rbim-donut-legend-value { fill:#173968; font-size:11px; font-weight:700; }\n.rbim-chart-zero { stroke:#9baeba; stroke-width:1; }\n.rbim-chart-track { fill:#edf2f5; }.rbim-chart-bar { fill:#176453; }\n.rbim-donut-track { fill:none; stroke:#edf2f5; stroke-width:30; }\ncircle.rbim-donut-a,circle.rbim-donut-b,circle.rbim-donut-c,circle.rbim-donut-d { fill:none; stroke-width:30; transform:rotate(-90deg); transform-origin:125px 125px; }\ncircle.rbim-donut-a { stroke:#176453; } circle.rbim-donut-b { stroke:#2e78a6; }\ncircle.rbim-donut-c { stroke:#d4a43e; } circle.rbim-donut-d { stroke:#7e5c9e; }\nrect.rbim-donut-a { fill:#176453; } rect.rbim-donut-b { fill:#2e78a6; }\nrect.rbim-donut-c { fill:#d4a43e; } rect.rbim-donut-d { fill:#7e5c9e; }\n.rbim-line-path { fill:none; stroke:#176453; stroke-width:3; }\n.rbim-line-point { fill:#fff; stroke:#176453; stroke-width:3; }\n.rbim-donut-total-label { fill:#728096; font-size:11px; }\n.rbim-donut-total { fill:#173968; font-size:22px; font-weight:800; }\n.rbim-analytics-table-wrap { margin-top:12px; page-break-inside:auto; }\n.rbim-analytics-table-wrap h3 { font-size:12pt; }\n.rbim-analytics-table { border-collapse:collapse; width:100%; min-width:0; }\n.rbim-analytics-table th,.rbim-analytics-table td { border:1px solid #aebdca; padding:5px; font-size:8pt; overflow-wrap:anywhere; }\n.rbim-analytics-table tr { break-inside:avoid; }\n.rbim-chart-warning { color:#76521a; }\n";
  report.head.appendChild(style);
  const heading = report.createElement("h1");
  heading.textContent = "RBIM Visual Analytics Report";
  report.body.appendChild(heading);
  const content = report.importNode(panel, true);
  content.querySelectorAll(".rbim-analytics-actions, .rbim-chart-download, button").forEach((control) => control.remove());
  report.body.appendChild(content);
  popup.setTimeout(() => { popup.focus(); popup.print(); }, 300);
}

async function downloadSvgAsPng(svg: SVGSVGElement, title: string) {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = `
    .rbim-chart-title{fill:#173968;font-size:14px;font-weight:700}.rbim-chart-zero{stroke:#9baeba;stroke-width:1}.rbim-chart-label{fill:#53677c;font-size:12px}
    .rbim-chart-value{fill:#173968;font-size:10px;font-weight:700}
    .rbim-chart-track{fill:#edf2f5}.rbim-chart-bar{fill:#176453}
    .rbim-donut-track{fill:none;stroke:#edf2f5;stroke-width:30}
    circle.rbim-donut-a,circle.rbim-donut-b,circle.rbim-donut-c,circle.rbim-donut-d{fill:none;stroke-width:30;transform:rotate(-90deg);transform-origin:125px 125px}
    circle.rbim-donut-a{stroke:#176453}circle.rbim-donut-b{stroke:#2e78a6}circle.rbim-donut-c{stroke:#d4a43e}circle.rbim-donut-d{stroke:#7e5c9e}
    rect.rbim-donut-a{fill:#176453}rect.rbim-donut-b{fill:#2e78a6}rect.rbim-donut-c{fill:#d4a43e}rect.rbim-donut-d{fill:#7e5c9e}
    .rbim-donut-total-label{fill:#728096;font-size:11px}.rbim-donut-total{fill:#173968;font-size:22px;font-weight:800}
    .rbim-donut-legend-label{fill:#53677c;font-size:11px}.rbim-donut-legend-value{fill:#173968;font-size:11px;font-weight:700}
    .rbim-line-path{fill:none;stroke:#176453;stroke-width:3}.rbim-line-point{fill:#fff;stroke:#176453;stroke-width:3}
  `;
  clone.prepend(style);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const xml = new XMLSerializer().serializeToString(clone);
  const source = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(source);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Chart image could not be prepared."));
      image.src = url;
    });
    const width = Math.max(720, svg.viewBox.baseVal.width || svg.clientWidth || 720);
    const height = Math.max(260, svg.viewBox.baseVal.height || svg.clientHeight || 260);
    const canvas = document.createElement("canvas");
    canvas.width = width * 2;
    canvas.height = height * 2;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.scale(2, 2);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    canvas.toBlob((png) => { if (png) downloadBlob(png, `${safeFileName(title)}.png`); }, "image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatCell(value: string | number | undefined, format: "TEXT" | "NUMBER", unit?: LiraVisualization["unit"]) {
  if (value === undefined) return "Unavailable";
  return format === "NUMBER" && typeof value === "number" ? formatLiraValue(value, unit) : String(value);
}
function formatNumber(value: number) { return value.toLocaleString("en-PH"); }
function formatDateTime(value: string) { const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }); }
function shortLabel(value: string, maximum: number) { return value.length <= maximum ? value : `${value.slice(0, maximum - 1)}…`; }
function safeFileName(value: string) { return value.replace(/[^a-z0-9_-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "rbim-report"; }
function tsvCell(value: string) { return /[\t\r\n"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value; }
